/**
 * =========================================================
 * Eco Traders • ET Quiz Engine (Frontend)
 * File: assets/et-quiz.js
 *
 * v3.3 (Restore thumbnail+excerpt markup + keep 1-up; layout via CSS)
 *
 * Why this version
 * - Your v3.2 markup change caused the theme/blog CSS to stop recognizing
 *   the thumbnail/excerpt structure (so image/excerpt disappeared).
 * - This version restores the richer, proven markup:
 *     .related-article-media + .related-article-text (+ title/excerpt)
 *   which reliably renders image + excerpt.
 * - Still renders ONLY 1 item (NEXT_STEPS_LIMIT = 1).
 *
 * NOTE (important)
 * - This JS outputs correct content. The final "text right of image"
 *   alignment should be enforced in et-quiz.css (scoped) so we don't
 *   fight blog CSS assumptions. (I gave you the CSS patch earlier.)
 * =========================================================
 */
(() => {
  "use strict";

  // ---------------------------------------------------------
  // Version beacon (proves which asset is running)
  // ---------------------------------------------------------
  window.__ET_QUIZ_VERSION = "v3.3-related-articles-rich-markup-1up";
  // eslint-disable-next-line no-console
  console.log("[ET QUIZ]", window.__ET_QUIZ_VERSION);

  /**
   * Render limit for Next steps cards
   * Set to 1 to match blog reference.
   */
  const NEXT_STEPS_LIMIT = 1;

  /**
   * ---------------------------------------------------------
   * Utilities
   * ---------------------------------------------------------
   */

  function safeJsonParse(str, fallback) {
    try {
      if (!str || typeof str !== "string") return fallback;
      return JSON.parse(str);
    } catch (_e) {
      return fallback;
    }
  }

  function el(tag, opts = {}) {
    const node = document.createElement(tag);
    if (opts.className) node.className = opts.className;
    if (opts.attrs) {
      Object.entries(opts.attrs).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        node.setAttribute(k, String(v));
      });
    }
    if (opts.text !== undefined) node.textContent = String(opts.text);
    return node;
  }

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function findPayloadScript(mountId) {
    return document.querySelector(
      `script[type="application/json"][data-et-quiz-payload="${CSS.escape(mountId)}"]`
    );
  }

  function fireGa4(payload, eventName, params) {
    const gaCfg = payload?.tracking?.ga4;
    const enabled = gaCfg?.enabled === true;
    if (!enabled) return;
    if (typeof window.gtag !== "function") return;

    try {
      window.gtag("event", eventName, params);
    } catch (_e) {
      // Never break UX due to analytics.
    }
  }

  function getQuizLocation() {
    return "page";
  }

  function getPathname() {
    return window.location?.pathname || "";
  }

  // ---------------------------------------------------------
  // App Proxy Configuration
  // ---------------------------------------------------------
  const APP_PROXY_BASE = "/apps/shop-app/api/proxy"; // Your app proxy path

  // ---------------------------------------------------------
  // Analytics Helper (NEW)
  // ---------------------------------------------------------
  function sendQuizEvent(eventType, data) {
    const endpoint = `${APP_PROXY_BASE}/quiz/${eventType}`;
    
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((r) => r.json())
      .then((result) => console.log(`[ET QUIZ] ${eventType} tracked:`, result))
      .catch((err) => console.warn(`[ET QUIZ] ${eventType} failed:`, err));
  }

  // Generate unique session ID
  function getSessionId() {
    let sid = sessionStorage.getItem("et_quiz_session");
    if (!sid) {
      sid = crypto.randomUUID ? crypto.randomUUID() : `sid_${Date.now()}_${Math.random()}`;
      sessionStorage.setItem("et_quiz_session", sid);
    }
    return sid;
  }

  /**
   * ---------------------------------------------------------
   * URL helpers (critical for inventory hydration)
   * ---------------------------------------------------------
   */

  function normalizeUrlKey(inputUrl) {
    try {
      const u = new URL(String(inputUrl || ""), window.location.origin);
      let p = u.pathname || "";
      if (p.length > 1) p = p.replace(/\/+$/, "");
      return p;
    } catch (_e) {
      const s = String(inputUrl || "").split("?")[0].split("#")[0];
      if (!s) return "";
      return s.length > 1 ? s.replace(/\/+$/, "") : s;
    }
  }

  function findInventoryByUrl(inventory, url) {
    if (!inventory || typeof inventory !== "object") return null;

    if (inventory[url]) return inventory[url];

    const key = normalizeUrlKey(url);
    if (key && inventory[key]) return inventory[key];

    if (!key) return null;
    const keys = Object.keys(inventory);
    for (const k of keys) {
      if (normalizeUrlKey(k) === key) return inventory[k];
    }
    return null;
  }

  function findReferencedByUrl(refs, url) {
    if (!Array.isArray(refs) || !url) return null;
    const key = normalizeUrlKey(url);
    if (!key) return null;

    for (const a of refs) {
      if (!a?.url) continue;
      if (normalizeUrlKey(a.url) === key) return a;
    }
    return null;
  }

  /**
   * ---------------------------------------------------------
   * Shortcode placement: [QUIZ id="sleep-style"]
   * ---------------------------------------------------------
   */

  function findQuizShortcodeSlot(expectedId) {
    const root = document.querySelector("#MainContent") || document.body;
    if (!root) return null;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const re = /\[QUIZ\s+id\s*=\s*["']([^"']+)["']\s*\]/i;

    let node;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue;
      if (!text || text.indexOf("[QUIZ") === -1) continue;

      const match = text.match(re);
      if (!match) continue;

      const foundId = match[1];

      if (expectedId && foundId && foundId !== expectedId) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ET Quiz] Shortcode id "${foundId}" does not match mounted quiz "${expectedId}". Using shortcode location.`
        );
      }

      const slot = document.createElement("span");
      slot.setAttribute("data-et-quiz-slot", foundId || expectedId || "");

      const before = document.createTextNode(text.slice(0, match.index));
      const after = document.createTextNode(text.slice(match.index + match[0].length));

      const parent = node.parentNode;
      if (!parent) return null;

      parent.insertBefore(before, node);
      parent.insertBefore(slot, node);
      parent.insertBefore(after, node);
      parent.removeChild(node);

      return slot;
    }

    return null;
  }

  function moveMountToSlot(mount, slot) {
    if (!mount || !slot) return;
    const parent = slot.parentNode;
    if (!parent) return;

    parent.insertBefore(mount, slot);
    parent.removeChild(slot);
  }

  /**
   * ---------------------------------------------------------
   * Quiz definition parsing (metaobject JSON strings)
   * ---------------------------------------------------------
   */

  function parseQuizDefinition(payload) {
    const questionsRaw = safeJsonParse(payload?.quiz?.questions_json_str, { questions: [] });
    const resultsRaw = safeJsonParse(payload?.quiz?.results_json_str, { profiles: [] });

    const questions = Array.isArray(questionsRaw?.questions) ? questionsRaw.questions : [];
    const profiles = Array.isArray(resultsRaw?.profiles) ? resultsRaw.profiles : [];

    return { questions, profiles };
  }

  function chooseProfile(tagSet, profiles) {
    if (!profiles || profiles.length === 0) return null;

    function evalProfile(profile) {
      const rule = profile?.rule || {};
      const mustInclude = Array.isArray(rule.must_include) ? rule.must_include : [];
      const anyOf = Array.isArray(rule.any_of) ? rule.any_of : [];
      const mustExclude = Array.isArray(rule.must_exclude) ? rule.must_exclude : [];

      for (const t of mustExclude) if (tagSet.has(t)) return { ok: false, score: 0 };
      for (const t of mustInclude) if (!tagSet.has(t)) return { ok: false, score: 0 };

      if (anyOf.length > 0) {
        let found = false;
        for (const t of anyOf) {
          if (tagSet.has(t)) {
            found = true;
            break;
          }
        }
        if (!found) return { ok: false, score: 0 };
      }

      let score = 0;
      for (const t of mustInclude) if (tagSet.has(t)) score += 2;
      for (const t of anyOf) if (tagSet.has(t)) score += 1;

      return { ok: true, score };
    }

    let best = null;
    let bestScore = -1;

    for (const p of profiles) {
      const { ok, score } = evalProfile(p);
      if (!ok) continue;
      if (score > bestScore) {
        best = p;
        bestScore = score;
      }
    }

    return best || profiles[0] || null;
  }

  /**
   * ---------------------------------------------------------
   * UI: Hydrate the Liquid shell (preferred)
   * ---------------------------------------------------------
   */

  class QuizUI {
    constructor(mount, payload, questions, profiles) {
      this.mount = mount;
      this.payload = payload;

      this.quizId = payload?.quiz?.id || mount.dataset.etQuizId || "quiz";
      this.location = getQuizLocation(payload);
      this.path = payload?.context?.path || getPathname();

      this.questions = Array.isArray(questions) ? questions : [];
      this.profiles = Array.isArray(profiles) ? profiles : [];

      this.referencedArticles = Array.isArray(payload?.quiz?.referenced_articles)
        ? payload.quiz.referenced_articles
        : [];

      this.articleInventory =
        payload?.quiz?.article_inventory && typeof payload.quiz.article_inventory === "object"
          ? payload.quiz.article_inventory
          : {};

      this.currentIndex = 0;
      this.started = false;
      this.completed = false;

      this.answersByQId = new Map();

      this.ui = null;
      this.body = null;
      this.progressText = null;
      this.progressBarOuter = null;
      this.progressFill = null;
      this.controls = null;
      this.btnBack = null;
      this.btnRestart = null;
      this.liveRegion = null;

      this.questionTitle = null;
      this.answersWrap = null;

      if (this.questions.length === 0) {
        this.renderFatalError("Quiz configuration is missing questions.");
        return;
      }

      this.connectOrBuildShell();
      this.renderStep();
      this.bindControlHandlers();
      this.mount.dataset.etQuizReady = "true";
    }

    connectOrBuildShell() {
      this.ui = this.mount.querySelector(".et-quiz__ui");

      if (this.ui) {
        this.body = this.ui.querySelector(".et-quiz__body");
        this.progressText = this.ui.querySelector(".et-quiz__progress-text");
        this.progressBarOuter = this.ui.querySelector(".et-quiz__progress-bar");
        this.progressFill = this.ui.querySelector(".et-quiz__progress-bar-fill");
        this.controls = this.ui.querySelector(".et-quiz__controls");
        this.btnBack =
          this.ui.querySelector(".et-quiz__btn--back") ||
          this.ui.querySelector(".et-quiz__btn.et-quiz__btn--back");
        this.btnRestart =
          this.ui.querySelector(".et-quiz__btn--restart") ||
          this.ui.querySelector(".et-quiz__btn.et-quiz__btn--restart");
        this.liveRegion = this.ui.querySelector(".et-quiz__sr-live");

        if (!this.body || !this.progressText || !this.progressFill || !this.controls) {
          // eslint-disable-next-line no-console
          console.warn("[ET QUIZ] Liquid shell incomplete. Falling back to JS-built shell.");
          this.buildFallbackShell();
        }

        return;
      }

      this.buildFallbackShell();
    }

    buildFallbackShell() {
      this.mount.innerHTML = "";

      this.ui = el("div", { className: "et-quiz__ui" });

      const header = el("header", { className: "et-quiz__header" });
      const titleText = this.payload?.quiz?.title || "Quiz";
      const introText = this.payload?.quiz?.intro || "";

      header.appendChild(el("h2", { className: "et-quiz__title", text: titleText }));
      if (introText) header.appendChild(el("p", { className: "et-quiz__intro", text: introText }));

      const progress = el("div", { className: "et-quiz__progress", attrs: { "aria-label": "Quiz progress" } });
      this.progressText = el("p", { className: "et-quiz__progress-text", text: "Question 1 of 1" });

      this.progressBarOuter = el("div", {
        className: "et-quiz__progress-bar",
        attrs: {
          role: "progressbar",
          "aria-valuemin": "0",
          "aria-valuemax": "100",
          "aria-valuenow": "0",
          "aria-label": "Progress"
        }
      });

      this.progressFill = el("div", { className: "et-quiz__progress-bar-fill" });
      this.progressBarOuter.appendChild(this.progressFill);

      progress.appendChild(this.progressText);
      progress.appendChild(this.progressBarOuter);

      this.body = el("div", { className: "et-quiz__body", attrs: { "aria-live": "polite" } });

      this.controls = el("div", { className: "et-quiz__controls", attrs: { "aria-label": "Quiz controls" } });
      this.btnBack = el("button", {
        className: "et-quiz__btn et-quiz__btn--back",
        attrs: { type: "button", disabled: "true" },
        text: "Back"
      });
      this.btnRestart = el("button", {
        className: "et-quiz__btn et-quiz__btn--restart",
        attrs: { type: "button" },
        text: "Restart"
      });
      this.controls.appendChild(this.btnBack);
      this.controls.appendChild(this.btnRestart);

      this.liveRegion = el("div", {
        className: "et-quiz__sr-live",
        attrs: { "aria-live": "polite", "aria-atomic": "true" }
      });

      this.ui.appendChild(header);
      this.ui.appendChild(progress);
      this.ui.appendChild(this.body);
      this.ui.appendChild(this.controls);
      this.ui.appendChild(this.liveRegion);

      this.mount.appendChild(this.ui);
    }

    renderFatalError(msg) {
      this.mount.innerHTML = "";
      const box = el("div", { className: "helper" });
      box.appendChild(el("p", { text: `Quiz unavailable: ${msg}` }));
      this.mount.appendChild(box);
    }

    updateProgress() {
      const total = this.questions.length;
      const idx = clamp(this.currentIndex, 0, total - 1);

      const stepNumber = clamp(idx + 1, 1, total);
      const pct = clamp(Math.round((stepNumber / total) * 100), 0, 100);

      if (this.progressText) this.progressText.textContent = `Question ${stepNumber} of ${total}`;
      if (this.progressFill) this.progressFill.style.width = `${pct}%`;

      if (this.progressBarOuter && this.progressBarOuter.getAttribute("role") === "progressbar") {
        this.progressBarOuter.setAttribute("aria-valuenow", String(pct));
      }
    }

    renderStep() {
      this.completed = false;
      this.currentIndex = clamp(this.currentIndex, 0, this.questions.length - 1);

      if (!this.started) {
        this.started = true;
        fireGa4(this.payload, this.payload?.tracking?.ga4?.events?.start || "et_quiz_start", {
          quiz_id: this.quizId,
          article_slug: this.path,
          quiz_location: this.location
        });

          sendQuizEvent("start", {
            quiz_id: this.quizId,
            session_id: getSessionId(),
          });
      }

      this.updateProgress();

      const q = this.questions[this.currentIndex];
      const qId = q?.id || `q_${this.currentIndex + 1}`;
      const prompt = q?.prompt || "Question";
      const answers = Array.isArray(q?.answers) ? q.answers : [];

      if (!this.body) {
        this.renderFatalError("Quiz UI body is missing.");
        return;
      }

      this.body.innerHTML = "";

      this.questionTitle = el("h3", {
        className: "et-quiz__question",
        attrs: { id: `${this.mount.id}-q` },
        text: prompt
      });

      this.answersWrap = el("div", {
        className: "et-quiz__answers",
        attrs: { role: "group", "aria-labelledby": `${this.mount.id}-q` }
      });

      if (answers.length === 0) {
        const box = el("div", { className: "helper" });
        box.appendChild(el("p", { text: `Quiz configuration error: question "${qId}" has no answers.` }));
        this.body.appendChild(box);
        return;
      }

      const selected = this.answersByQId.get(qId)?.answerLabel || null;

      answers.forEach((a) => {
        const label = typeof a?.label === "string" ? a.label : "";
        const tags = Array.isArray(a?.tags) ? a.tags : [];

        const btn = el("button", {
          className: "et-quiz__answer",
          attrs: { type: "button", "data-qid": qId, "aria-pressed": selected === label ? "true" : "false" },
          text: label
        });

        btn.addEventListener("click", () => this.selectAnswer(qId, label, tags));
        this.answersWrap.appendChild(btn);
      });

      this.body.appendChild(this.questionTitle);
      this.body.appendChild(this.answersWrap);

      if (this.btnBack) this.btnBack.disabled = this.currentIndex === 0;

      this.focusHeading(this.questionTitle);
    }

    selectAnswer(qId, label, tags) {
      this.answersByQId.set(qId, { answerLabel: label, tags: tags.slice(0) });

      if (this.answersWrap) {
        const btns = this.answersWrap.querySelectorAll("button.et-quiz__answer");
        btns.forEach((b) => {
          const isThis = b.getAttribute("data-qid") === qId && b.textContent === label;
          b.setAttribute("aria-pressed", isThis ? "true" : "false");
        });
      }

      this.announce(`Selected: ${label}`);

      fireGa4(this.payload, this.payload?.tracking?.ga4?.events?.answer || "et_quiz_answer", {
        quiz_id: this.quizId,
        q_id: qId,
        answer: label,
        article_slug: this.path,
        quiz_location: this.location
      });

      sendQuizEvent("answer", {
        session_id: getSessionId(),
        quiz_id: this.quizId,
        question_id: qId,
        answer_key: label,
      });

      const isLast = this.currentIndex >= this.questions.length - 1;
      if (isLast) {
        this.completeQuiz();
        return;
      }

      this.currentIndex += 1;
      this.renderStep();
    }

    goBack() {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.renderStep();
    }

    restart() {
      this.currentIndex = 0;
      this.answersByQId.clear();
      this.started = false;
      this.completed = false;
      this.renderStep();
      this.announce("Quiz restarted.");
    }

    buildTagSet() {
      const set = new Set();
      for (const [, v] of this.answersByQId.entries()) {
        const tags = Array.isArray(v?.tags) ? v.tags : [];
        tags.forEach((t) => {
          if (typeof t === "string" && t.trim()) set.add(t.trim());
        });
      }
      return set;
    }

    completeQuiz() {
      this.completed = true;

      fireGa4(this.payload, this.payload?.tracking?.ga4?.events?.complete || "et_quiz_complete", {
        quiz_id: this.quizId,
        article_slug: this.path,
        quiz_location: this.location
      });

      const tagSet = this.buildTagSet();
      const profile = chooseProfile(tagSet, this.profiles);

      if (profile?.id) {
        fireGa4(this.payload, this.payload?.tracking?.ga4?.events?.result || "et_quiz_result", {
          quiz_id: this.quizId,
          profile_id: profile.id,
          article_slug: this.path,
          quiz_location: this.location
        });

         sendQuizEvent("complete", {
          session_id: getSessionId(),
          quiz_id: this.quizId,
          result_profile: profile.id,
        });
      }

      this.renderResult(profile);
      this.announce("Quiz complete. Showing your result.");
    }

    /**
     * ---------------------------------------------------------
     * Next steps (RESTORED: thumbnail + text wrappers)
     * ---------------------------------------------------------
     * We intentionally use the wrapper structure that already proved
     * it renders image + excerpt. Layout is then handled by CSS (scoped).
     */
    renderNextStepsAsRelatedArticles(items) {
      if (!Array.isArray(items) || items.length === 0) return null;

      const aside = el("aside", {
        className: "related-articles-block",
        attrs: { "data-et-related": "true", "aria-label": "Related Articles" }
      });

      aside.appendChild(el("h3", { text: "Helpful Articles" }));

      const ul = el("ul", { className: "related-articles-list" });

      items.slice(0, NEXT_STEPS_LIMIT).forEach((a) => {
        if (!a?.url || !a?.title) return;

        const li = el("li", { className: "related-article-item" });

        const link = el("a", {
          className: "related-article-link",
          attrs: { href: String(a.url) }
        });

// Blog-compatible thumbnail wrapper (REQUIRED)
if (a.image) {
  const fig = el("figure", { className: "related-article-thumb" });
  const img = el("img", {
    attrs: {
      src: String(a.image),
      alt: String(a.alt || a.title),
      loading: "lazy",
      width: "96",
      height: "96"
    }
  });
  fig.appendChild(img);
  link.appendChild(fig);
}


        // Text wrapper
        const textWrap = el("div", { className: "related-article-text" });
        textWrap.appendChild(el("p", { className: "related-article-title", text: String(a.title) }));

        if (a.excerpt) {
          textWrap.appendChild(el("p", { className: "related-article-excerpt", text: String(a.excerpt) }));
        }

        link.appendChild(textWrap);


        li.appendChild(link);
        ul.appendChild(li);
      });

      aside.appendChild(ul);
      return aside;
    }

buildNextStepsItems(profile) {
  // Always render from Referenced Articles (single, authoritative source)
  if (!Array.isArray(this.referencedArticles) || !this.referencedArticles.length) {
    return [];
  }

  const a = this.referencedArticles[0];

  return [{
    url: String(a.url),
    title: String(a.title),
    image: a.image ? String(a.image) : undefined,
    alt: String(a.alt || a.title),
    excerpt: a.excerpt ? String(a.excerpt) : undefined
  }];
}


    renderResult(profile) {
      if (this.progressText) this.progressText.textContent = "Complete";
      if (this.progressFill) this.progressFill.style.width = "100%";
      if (this.progressBarOuter && this.progressBarOuter.getAttribute("role") === "progressbar") {
        this.progressBarOuter.setAttribute("aria-valuenow", "100");
      }
      if (this.btnBack) this.btnBack.disabled = true;

      if (!this.body) return;
      this.body.innerHTML = "";

      const wrap = el("div", { className: "et-quiz__result" });

      wrap.appendChild(
        el("p", {
          className: "et-quiz__result-reassurance",
          text:
            "There’s no single “right” sleep style — most people are a mix. This result highlights the pattern you most closely matched."
        })
      );

      const title = el("h3", { className: "et-quiz__result-title", text: profile?.title || "Your sleep style" });
      wrap.appendChild(title);

      if (profile?.summary) {
        wrap.appendChild(el("p", { className: "et-quiz__result-summary", text: String(profile.summary) }));
      }

      const helps = Array.isArray(profile?.what_helps) ? profile.what_helps : [];
      if (helps.length > 0) {
        wrap.appendChild(el("h4", { className: "et-quiz__result-subhead", text: "What often helps" }));
        const ul = el("ul", { className: "key-list et-quiz__result-list" });
        helps.slice(0, 6).forEach((item) => ul.appendChild(el("li", { text: String(item) })));
        wrap.appendChild(ul);
      }

      const nextItems = this.buildNextStepsItems(profile);
      if (nextItems.length > 0) {
        const relatedBlock = this.renderNextStepsAsRelatedArticles(nextItems);
        if (relatedBlock) wrap.appendChild(relatedBlock);
      }

      const actions = el("div", { className: "et-quiz__result-actions" });
      const btnAgain = el("button", {
        className: "et-quiz__btn et-quiz__btn--primary",
        attrs: { type: "button" },
        text: "Retake quiz"
      });
      btnAgain.addEventListener("click", () => this.restart());
      actions.appendChild(btnAgain);
      wrap.appendChild(actions);

      this.body.appendChild(wrap);
      this.focusHeading(title);
    }

    announce(msg) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = msg;
    }

    bindControlHandlers() {
      if (this.btnBack) this.btnBack.addEventListener("click", () => this.goBack());
      if (this.btnRestart) this.btnRestart.addEventListener("click", () => this.restart());
    }

    focusHeading(heading) {
      if (!heading) return;
      heading.setAttribute("tabindex", "-1");
      try {
        heading.focus({ preventScroll: true });
      } catch (_e) {
        heading.focus();
      }
      window.setTimeout(() => heading.removeAttribute("tabindex"), 0);
    }
  }

  /**
   * ---------------------------------------------------------
   * Boot
   * ---------------------------------------------------------
   */

  function initMount(mount) {
    const mountId = mount.id;
    if (!mountId) return;

    const script = findPayloadScript(mountId);
    if (!script) return;

    const payloadText = script.textContent || "";
    const payload = safeJsonParse(payloadText, null);

    if (!payload || !payload.quiz) {
      mount.innerHTML = "";
      const box = el("div", { className: "helper" });
      box.appendChild(el("p", { text: "Quiz unavailable: invalid payload." }));
      mount.appendChild(box);
      return;
    }

    const expectedId = payload?.quiz?.id || mount.dataset.etQuizId || "";
    const slot = findQuizShortcodeSlot(expectedId);
    if (slot) moveMountToSlot(mount, slot);

    const { questions, profiles } = parseQuizDefinition(payload);

    questions.forEach((q, idx) => {
      if (!q.id) q.id = `q_${idx + 1}`;
    });

    // eslint-disable-next-line no-new
    new QuizUI(mount, payload, questions, profiles);
  }

  function initAll() {
    const mounts = Array.from(document.querySelectorAll('[data-et-quiz-mount="true"]'));
    if (mounts.length === 0) return;

    if (mounts.length > 1) {
      // eslint-disable-next-line no-console
      console.warn(`[ET QUIZ] Found ${mounts.length} quiz mounts. Initializing first only (editorial rule: one quiz per page).`);
    }

    try {
      initMount(mounts[0]);
    } catch (_e) {
      // Never break the page if quiz fails.
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
})();
